import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Mail, User as UserIcon, Sparkles, Lock, CheckCircle2,
  AlertCircle, Loader2, ExternalLink, Clock, DollarSign, Send,
} from "lucide-react";
import AdminShell from "../components/AdminShell";
import { fetchAdminCustomer, sendAdminCustomerWelcome } from "../lib/analytics";

/**
 * AdminCustomer — /admin/customers/:userId
 *
 * Founder-only customer detail page. Surfaces decrypted email/name +
 * Pro/founding attribution + Stripe link + lifetime revenue + a
 * one-click "Send welcome email" button. Every access is audit-logged
 * server-side.
 *
 * Design note: this is intentionally information-dense (not pretty
 * marketing UI) because it's a founder-ops tool — when revenue lands,
 * you want every relevant field in one view, not a hero card.
 */

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  }) : "—";

const fmtMoney = (n, ccy = "USD") => {
  if (typeof n !== "number") return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency: ccy,
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
};

const Pill = ({ tone = "neutral", children, testid }) => {
  const tones = {
    neutral: "bg-white/[0.06] text-white/70 border-white/10",
    good: "bg-[#7BAE8E]/15 text-[#A5D3B5] border-[#7BAE8E]/30",
    warn: "bg-[#D4B483]/15 text-[#E0C99B] border-[#D4B483]/30",
    bad: "bg-[#F4B8C0]/15 text-[#F4B8C0] border-[#F4B8C0]/30",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] border rounded-full px-2.5 py-0.5 ${tones[tone]}`}
      data-testid={testid}
    >
      {children}
    </span>
  );
};

const Field = ({ label, value, testid, mono = false, full = false }) => (
  <div className={full ? "col-span-2" : ""}>
    <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1">
      {label}
    </div>
    <div
      className={`text-sm text-white ${mono ? "font-mono text-xs" : ""} break-all`}
      data-testid={testid}
    >
      {value || <span className="text-white/35">—</span>}
    </div>
  </div>
);

export default function AdminCustomer() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Welcome email send state
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [sendError, setSendError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchAdminCustomer(userId);
      setData(d);
    } catch (e) {
      setError(
        e?.response?.status === 404 ? "Customer not found" :
        e?.response?.data?.detail || "Couldn't load customer"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleSendWelcome = async (force = false) => {
    setSending(true);
    setSendError(null);
    setSendResult(null);
    try {
      const r = await sendAdminCustomerWelcome(userId, force);
      setSendResult(r);
      // Re-fetch to update the welcome_email_sent_at field
      await load();
    } catch (e) {
      setSendError(
        e?.response?.data?.detail || "Send failed — try again."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminShell
      eyebrow="Customer detail"
      title={
        data?.name ||
        data?.email ||
        (userId ? userId.slice(0, 24) + "…" : "Customer")
      }
      subtitle={
        <span>
          Audit-logged · admin-only ·{" "}
          <Link to="/admin" className="underline hover:text-white">
            ← back to admin
          </Link>
        </span>
      }
      actions={
        <Link
          to="/admin/revenue"
          className="text-xs rounded-full px-3 py-1.5 bg-white/[0.06] text-white/70 hover:text-white hover:bg-white/[0.12] border border-white/10 transition-colors inline-flex items-center gap-1.5"
        >
          <ArrowLeft className="h-3 w-3" />
          Revenue
        </Link>
      }
    >
      {loading && (
        <div className="flex items-center gap-2 text-white/50">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading customer…
        </div>
      )}

      {error && !loading && (
        <div
          className="bg-[#F4B8C0]/10 border border-[#F4B8C0]/30 rounded-2xl p-5 text-[#F4B8C0]"
          data-testid="customer-error"
        >
          <AlertCircle className="h-5 w-5 inline mr-2 -mt-0.5" />
          {error}
        </div>
      )}

      {data && (
        <div data-testid="admin-customer-page">
          {/* ── Identity card ──────────────────────────────────── */}
          <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 sm:p-7 mb-6">
            <div className="flex items-start gap-4 mb-5">
              <div className="h-14 w-14 rounded-full bg-[#9B6A6C]/20 border border-[#9B6A6C]/30 flex items-center justify-center text-2xl text-[#D4B483] font-light">
                {(data.name || data.email || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <h2
                  className="text-2xl font-light text-white truncate"
                  data-testid="customer-name"
                >
                  {data.name || "(no name on file)"}
                </h2>
                <a
                  href={data.email ? `mailto:${data.email}` : undefined}
                  className="text-sm text-[#D4B483] hover:underline inline-flex items-center gap-1.5"
                  data-testid="customer-email"
                >
                  <Mail className="h-3.5 w-3.5" />
                  {data.email || "no email decryptable"}
                </a>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {data.is_pro && (
                    <Pill tone="good" testid="badge-pro">
                      <CheckCircle2 className="h-3 w-3" />
                      Pro
                    </Pill>
                  )}
                  {data.founding_lock_in && (
                    <Pill tone="warn" testid="badge-lockin">
                      <Lock className="h-3 w-3" />
                      Founding lock-in
                    </Pill>
                  )}
                  {data.is_founding_member && !data.founding_lock_in && (
                    <Pill tone="warn" testid="badge-founding">
                      <Sparkles className="h-3 w-3" />
                      Founding member
                    </Pill>
                  )}
                  {data.is_day_one && (
                    <Pill tone="warn">Day-one</Pill>
                  )}
                  {data.stealth_default && (
                    <Pill testid="badge-stealth">Stealth default</Pill>
                  )}
                  {!data.email_verified && (
                    <Pill tone="bad">Email unverified</Pill>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 pt-5 border-t border-white/[0.08]">
              <Field label="user_id" value={data.user_id} mono testid="field-user-id" />
              <Field label="Auth provider" value={data.auth_provider} testid="field-auth-provider" />
              <Field label="Signed up" value={fmtDate(data.created_at)} testid="field-created-at" />
              <Field label="Last activity" value={fmtDate(data.last_activity_at)} testid="field-last-activity" />
              <Field label="Routines lifetime" value={data.routines_total} testid="field-routines-total" />
              <Field label="Pro until" value={fmtDate(data.pro_until)} testid="field-pro-until" />
            </div>
          </div>

          {/* ── Revenue card ───────────────────────────────────── */}
          <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 sm:p-7 mb-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-medium text-white inline-flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-[#D4B483]" />
                Payments
              </h3>
              {data.stripe_customer_id && (
                <a
                  href={`https://dashboard.stripe.com/customers/${data.stripe_customer_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-[#D4B483] hover:underline inline-flex items-center gap-1"
                  data-testid="stripe-link"
                >
                  Open in Stripe
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 mb-5">
              <Field
                label="Lifetime revenue"
                value={fmtMoney(data.lifetime_revenue)}
                testid="field-lifetime-revenue"
              />
              <Field
                label="Paid transactions"
                value={data.paid_transactions}
                testid="field-paid-count"
              />
              <Field
                label="Pro source"
                value={data.pro_source}
                testid="field-pro-source"
              />
              <Field
                label="Pro package"
                value={data.pro_package_id}
                testid="field-pro-package"
              />
              <Field
                label="Founding lock-in claimed at"
                value={fmtDate(data.founding_lock_in_at)}
                testid="field-lockin-at"
              />
              <Field
                label="Stripe customer"
                value={data.stripe_customer_id}
                mono
                testid="field-stripe-customer-id"
              />
            </div>
            {data.recent_paid && data.recent_paid.length > 0 && (
              <div className="pt-5 border-t border-white/[0.08]">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-3">
                  Recent paid transactions
                </div>
                <div className="space-y-2">
                  {data.recent_paid.map((t) => (
                    <div
                      key={t.session_id}
                      className="flex items-center justify-between text-sm bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-2.5"
                      data-testid={`recent-txn-${t.session_id}`}
                    >
                      <div>
                        <div className="text-white tabular-nums">
                          {fmtMoney(t.amount, t.currency)}
                          <span className="text-white/45 text-xs ml-2">
                            {t.package_id}
                          </span>
                          {t.came_via_lock_in && (
                            <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-[#D4B483]">
                              · /lock-in
                            </span>
                          )}
                        </div>
                        <div className="text-white/45 text-xs mt-0.5">
                          {fmtDate(t.created_at)}
                        </div>
                      </div>
                      <a
                        href={`https://dashboard.stripe.com/payments/${t.session_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-[#D4B483] hover:underline"
                      >
                        Stripe →
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Welcome email action ──────────────────────────── */}
          <div
            className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 sm:p-7 mb-6"
            data-testid="welcome-email-card"
          >
            <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
              <div>
                <h3 className="text-lg font-medium text-white inline-flex items-center gap-2 mb-1">
                  <Send className="h-4 w-4 text-[#D4B483]" />
                  Welcome email
                </h3>
                <p className="text-sm text-white/55">
                  A short, personal note from Iyal — auto-personalises with first name
                  and a founding-member line if applicable.
                </p>
              </div>
              {data.welcome_email_sent_at && (
                <Pill tone="good" testid="welcome-already-sent">
                  <Clock className="h-3 w-3" />
                  Sent {fmtDate(data.welcome_email_sent_at)}
                </Pill>
              )}
            </div>

            {sendResult && sendResult.status === "ok" && (
              <div
                className="bg-[#7BAE8E]/10 border border-[#7BAE8E]/30 rounded-xl p-3 mb-3 text-sm text-[#A5D3B5]"
                data-testid="welcome-sent-ok"
              >
                <CheckCircle2 className="h-4 w-4 inline mr-1.5 -mt-0.5" />
                Sent to {sendResult.sent_to}.
              </div>
            )}
            {sendResult && sendResult.status === "already_sent" && (
              <div
                className="bg-[#D4B483]/10 border border-[#D4B483]/30 rounded-xl p-3 mb-3 text-sm text-[#E0C99B]"
                data-testid="welcome-already-warning"
              >
                Already sent on {fmtDate(sendResult.sent_at)}. Use "Send again" if intentional.
              </div>
            )}
            {sendError && (
              <div
                className="bg-[#F4B8C0]/10 border border-[#F4B8C0]/30 rounded-xl p-3 mb-3 text-sm text-[#F4B8C0]"
                data-testid="welcome-send-error"
              >
                {sendError}
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => handleSendWelcome(false)}
                disabled={sending || !data.email}
                data-testid="send-welcome-btn"
                className="inline-flex items-center gap-2 bg-[#9B6A6C] hover:bg-[#5D4050] disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Send welcome email
              </button>
              {data.welcome_email_sent_at && (
                <button
                  onClick={() => handleSendWelcome(true)}
                  disabled={sending}
                  data-testid="send-welcome-force-btn"
                  className="inline-flex items-center gap-2 text-white/70 hover:text-white border border-white/15 hover:border-white/30 px-5 py-2.5 rounded-full text-sm font-medium transition-colors"
                >
                  Send again
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
